import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { ArrowRight, Loader2, ShieldCheck } from "lucide-react";

export default function ConsentRegolamento() {
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user === null) return;
    if (!user) {
      navigate("/login", { replace: true });
      return;
    }
    if (!user.needs_consent) {
      navigate(user.role === "admin" ? "/admin" : "/dashboard", { replace: true });
    }
  }, [user, navigate]);

  if (user === null || !user || !user.needs_consent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7F8F9]" data-testid="consent-page">
        <Loader2 className="w-6 h-6 animate-spin text-[#0F7B85]" />
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (!accepted) {
      toast.error("Devi accettare il regolamento per continuare");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/consent", { consent_regolamento: true });
      await refresh();
      toast.success("Consenso registrato con successo");
      navigate(user.role === "admin" ? "/admin" : "/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F7F8F9] p-6" data-testid="consent-page">
      <div className="w-full max-w-lg fade-up">
        <div className="bg-white border border-slate-200 rounded-2xl p-8 sm:p-10">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-[#0F7B85]/10 flex items-center justify-center">
              <ShieldCheck className="w-7 h-7 text-[#0F7B85]" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Ultimo passaggio</div>
              <h1 className="font-display text-2xl text-[#0F7B85] mt-0.5">Accetta il regolamento</h1>
            </div>
          </div>

          <p className="mt-6 text-sm text-slate-600 leading-relaxed">
            Ciao <strong>{user.name || user.email}</strong>, per attivare il tuo wallet
            &ldquo;Presenta un Amico&rdquo; devi accettare il regolamento ufficiale e dare il consenso
            al trattamento dati (GDPR).
          </p>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="consent-form">
            <label
              className="flex items-start gap-3 bg-[#F7F8F9] border border-slate-200 rounded-xl p-4 cursor-pointer hover:border-[#0F7B85] transition-colors"
              data-testid="consent-label"
            >
              <input
                type="checkbox"
                checked={accepted}
                onChange={(e) => setAccepted(e.target.checked)}
                data-testid="consent-checkbox"
                className="mt-1 w-4 h-4 rounded border-slate-300 text-[#0F7B85] focus:ring-[#0F7B85]"
              />
              <span className="text-sm text-slate-700 leading-relaxed">
                Dichiaro di aver letto e accettato il{" "}
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/regolamento.pdf`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#0F7B85] font-semibold hover:underline"
                  data-testid="consent-link"
                >
                  regolamento ufficiale &ldquo;Besafe ti premia&rdquo;
                </a>
                {" "}e il trattamento dei dati ai sensi del GDPR (Reg. UE 2016/679).
              </span>
            </label>

            <button
              type="submit"
              disabled={submitting || !accepted}
              data-testid="consent-submit-button"
              className="w-full bg-[#0F7B85] text-white hover:bg-[#0A5C64] disabled:opacity-60 rounded-lg px-6 py-3 font-medium transition-colors inline-flex items-center justify-center gap-2"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin"/> : <ArrowRight className="w-4 h-4" />}
              Accetta e continua
            </button>
          </form>

          <p className="mt-5 text-xs text-center text-slate-500">
            Il consenso viene registrato con data e ora ai fini legali (GDPR).
          </p>
          <div className="mt-6 pt-5 border-t border-slate-200 text-[10px] text-slate-400 leading-relaxed text-center">
            BeSafe Broker s.r.l. · P.IVA <span className="font-mono">07093890825</span> · RUI <strong>B000725320</strong> · Vigilanza IVASS
          </div>
        </div>
      </div>
    </div>
  );
}
